import { ApiClient } from "../api/ApiClient.js";
import { ENDPOINTS } from "../api/endpoints.js";
import { AuthError } from "../errors/AuthError.js";
import { BrowserStorage } from "../storage/BrowserStorage.js";
import { MemoryStorage } from "../storage/MemoryStorage.js";
import { normalizeRole } from "../types/user.js";
import { decodeJwt, getTokenExpiresIn } from "../utils/jwt.js";
/**
 * AuthClient - 認証フローとトークン管理を統合する中核クラス
 *
 * @example
 * ```typescript
 * const authClient = new AuthClient({
 *   baseUrl: 'http://localhost:3000',
 *   autoRefresh: true,
 *   onAuthError: (error) => {
 *     console.error('Auth error:', error);
 *     // ログインページへリダイレクト等
 *   }
 * });
 *
 * // ログイン
 * const response = await authClient.login({
 *   user_name: 'admin',
 *   password: 'admin_password'
 * });
 *
 * // 認証状態確認
 * if (authClient.isAuthenticated()) {
 *   const user = authClient.getCurrentUser();
 *   console.log('Logged in as:', user?.user_name);
 * }
 * ```
 */
export class AuthClient {
    config;
    apiClient;
    storage;
    refreshTimer;
    currentUser = null;
    /**
     * AuthClientを初期化します
     *
     * @param config - AuthClient設定
     */
    constructor(config) {
        // デフォルト値の設定
        this.config = {
            baseUrl: config.baseUrl,
            storage: config.storage || this.createDefaultStorage(),
            autoRefresh: config.autoRefresh ?? true,
            refreshThreshold: config.refreshThreshold ?? 60,
            onTokenRefresh: config.onTokenRefresh || (() => undefined),
            onAuthError: config.onAuthError || (() => undefined),
        };
        this.storage = this.config.storage;
        // ApiClientの初期化
        this.apiClient = new ApiClient({
            baseUrl: this.config.baseUrl,
            getAccessToken: () => this.getAccessToken(),
            onUnauthorized: () => {
                // 401エラー時はトークンをクリアしてログアウト状態にする
                this.clearAuthState();
            },
        });
        // 既存トークンのチェック（ページリロード時の復元）
        this.restoreAuthState();
    }
    /**
     * デフォルトストレージの作成
     * ブラウザ環境ではBrowserStorage、Node.js環境ではMemoryStorageを使用
     *
     * @returns TokenStorageInterface実装
     */
    createDefaultStorage() {
        // ブラウザ環境チェック
        if (typeof window !== "undefined" && window.localStorage) {
            return new BrowserStorage({ storageType: "localStorage" });
        }
        // Node.js環境（テスト等）
        return new MemoryStorage();
    }
    /**
     * 既存トークンから認証状態を復元します
     */
    restoreAuthState() {
        const accessToken = this.storage.getAccessToken();
        if (!accessToken) {
            return;
        }
        try {
            const payload = decodeJwt(accessToken);
            this.currentUser = {
                user_id: payload.sub,
                user_name: payload.user_name,
                role: normalizeRole(payload.role),
            };
            // 自動リフレッシュが有効な場合はスケジュールする
            if (this.config.autoRefresh) {
                this.scheduleTokenRefresh();
            }
        }
        catch {
            // トークンが無効な場合はクリア
            this.clearAuthState();
        }
    }
    /**
     * ログインを実行します
     *
     * @param credentials - ログイン認証情報（user_name, password）
     * @returns ログインレスポンス（トークン、ユーザー情報）
     * @throws {AuthError} 認証失敗、ネットワークエラー等
     */
    async login(credentials) {
        try {
            const response = await this.apiClient.post(ENDPOINTS.LOGIN, credentials);
            // トークンの保存
            this.storage.setTokens(response.access_token, response.refresh_token);
            // ユーザー情報の保存（ロールを小文字に正規化）
            this.currentUser = {
                ...response.user,
                role: normalizeRole(response.user.role),
            };
            // レスポンスのユーザー情報も正規化
            response.user.role = normalizeRole(response.user.role);
            // onTokenRefreshコールバックの呼び出し
            this.config.onTokenRefresh({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
            });
            // 自動リフレッシュが有効な場合はスケジュールする
            if (this.config.autoRefresh) {
                this.scheduleTokenRefresh();
            }
            return response;
        }
        catch (error) {
            // エラーハンドリング
            this.handleAuthError(error);
        }
    }
    /**
     * ログアウトを実行します
     *
     * @throws {AuthError} ログアウトAPI失敗時
     */
    async logout() {
        const refreshToken = this.storage.getRefreshToken();
        if (!refreshToken) {
            throw new AuthError("AUTH_001", "No refresh token found");
        }
        try {
            await this.apiClient.post(ENDPOINTS.LOGOUT, {
                refresh_token: refreshToken,
            });
        }
        finally {
            // APIエラーが発生してもトークンはクリアする
            this.clearAuthState();
        }
    }
    /**
     * トークンをリフレッシュします
     *
     * @returns リフレッシュレスポンス（新しいトークン）
     * @throws {AuthError} リフレッシュ失敗時
     */
    async refreshToken() {
        const refreshToken = this.storage.getRefreshToken();
        if (!refreshToken) {
            throw new AuthError("AUTH_001", "No refresh token found");
        }
        try {
            const request = { refresh_token: refreshToken };
            const response = await this.apiClient.post(ENDPOINTS.REFRESH, request);
            // トークンの更新
            this.storage.setTokens(response.access_token, response.refresh_token);
            // onTokenRefreshコールバックの呼び出し
            this.config.onTokenRefresh({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
            });
            // 自動リフレッシュが有効な場合はスケジュールを再設定
            if (this.config.autoRefresh) {
                this.scheduleTokenRefresh();
            }
            return response;
        }
        catch (error) {
            // リフレッシュ失敗時はトークンをクリアしてログアウト状態にする
            this.clearAuthState();
            this.handleAuthError(error);
        }
    }
    /**
     * アクセストークンの有効性を検証します
     *
     * @returns トークン検証レスポンス（valid、user、expires_at）
     * @throws {AuthError} 検証API失敗時
     */
    async verifyToken() {
        const accessToken = this.storage.getAccessToken();
        if (!accessToken) {
            throw new AuthError("AUTH_001", "No access token found");
        }
        try {
            const response = await this.apiClient.post(ENDPOINTS.VERIFY, {});
            return response;
        }
        catch (error) {
            throw this.handleAuthError(error);
        }
    }
    /**
     * アクセストークンを取得します
     *
     * @returns アクセストークン（認証されていない場合はnull）
     */
    getAccessToken() {
        return this.storage.getAccessToken();
    }
    /**
     * リフレッシュトークンを取得します
     *
     * @returns リフレッシュトークン（認証されていない場合はnull）
     */
    getRefreshToken() {
        return this.storage.getRefreshToken();
    }
    /**
     * 認証状態を確認します
     *
     * @returns 認証されている場合はtrue、そうでない場合はfalse
     */
    isAuthenticated() {
        const accessToken = this.storage.getAccessToken();
        return accessToken !== null && this.currentUser !== null;
    }
    /**
     * 現在のユーザー情報を取得します
     *
     * @returns ユーザー情報（認証されていない場合はnull）
     */
    getCurrentUser() {
        return this.currentUser;
    }
    /**
     * システムのヘルスチェックを実行します
     *
     * @returns ヘルスチェックレスポンス（status、timestamp、checks）
     * @throws {AuthError} ヘルスチェックAPI失敗時
     *
     * @example
     * ```typescript
     * const health = await authClient.checkHealth();
     * if (health.status === 'ok') {
     *   console.log('System is healthy');
     * }
     * ```
     */
    async checkHealth() {
        try {
            const response = await this.apiClient.get(ENDPOINTS.HEALTH);
            return response;
        }
        catch (error) {
            this.handleAuthError(error);
        }
    }
    /**
     * JWKS（JSON Web Key Set）を取得します
     *
     * JWT署名検証用の公開鍵情報を取得します。
     *
     * @returns JWKSレスポンス（公開鍵の配列）
     * @throws {AuthError} JWKS取得API失敗時
     *
     * @example
     * ```typescript
     * const jwks = await authClient.getJwks();
     * console.log('Public keys:', jwks.keys);
     * ```
     */
    async getJwks() {
        try {
            const response = await this.apiClient.get(ENDPOINTS.JWKS);
            return response;
        }
        catch (error) {
            this.handleAuthError(error);
        }
    }
    /**
     * 自動トークンリフレッシュをスケジュールします
     */
    scheduleTokenRefresh() {
        // 既存のタイマーをクリア
        this.clearTokenRefresh();
        const accessToken = this.storage.getAccessToken();
        if (!accessToken) {
            return;
        }
        try {
            const expiresIn = getTokenExpiresIn(accessToken);
            const refreshThreshold = this.config.refreshThreshold;
            // リフレッシュまでの待機時間を計算
            const delay = Math.max(0, (expiresIn - refreshThreshold) * 1000);
            this.refreshTimer = setTimeout(async () => {
                try {
                    await this.refreshToken();
                }
                catch (error) {
                    // リフレッシュ失敗時はonAuthErrorコールバックを呼び出す
                    if (error instanceof AuthError) {
                        this.config.onAuthError(error);
                    }
                }
            }, delay);
        }
        catch {
            // JWT解析エラー時はトークンをクリア
            this.clearAuthState();
        }
    }
    /**
     * 自動トークンリフレッシュのタイマーをクリアします
     */
    clearTokenRefresh() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }
    /**
     * 認証状態をクリアします（ログアウト・エラー時）
     */
    clearAuthState() {
        this.storage.clearTokens();
        this.currentUser = null;
        this.clearTokenRefresh();
    }
    /**
     * 認証エラーをハンドリングします
     *
     * @param error - エラーオブジェクト
     * @throws {AuthError} 必ず再スローする
     */
    handleAuthError(error) {
        let authError;
        if (error instanceof AuthError) {
            authError = error;
        }
        else if (error instanceof Error) {
            authError = new AuthError("NETWORK_ERROR", error.message);
        }
        else {
            authError = new AuthError("INTERNAL_001", "Unknown error occurred");
        }
        // onAuthErrorコールバックを呼び出す
        this.config.onAuthError(authError);
        // エラーを再スロー
        throw authError;
    }
}
//# sourceMappingURL=AuthClient.js.map